/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    userIds,
    labelId,
    labels,
    teamId,
    vipLevelId,
    issueId,
    priority,
    conversationType,
    sortBy,
    q,
    reportFrom,
    reportTo,
    previewMessagesCount,
    updatedWithin,
    includeMeta = true,
    includeSlaEvents = false,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        user_ids: userIds,
        label_id: labelId,
        team_id: teamId,
        vip_level_id: vipLevelId,
        issue_id: issueId,
        priority,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        q,
        report_from: reportFrom,
        report_to: reportTo,
        preview_messages_count: previewMessagesCount,
        updated_within: updatedWithin,
        include_meta: includeMeta,
        include_sla_events: includeSlaEvents,
      },
    });
  }

  show(conversationDisplayId) {
    return axios.get(`${this.url}/${conversationDisplayId}`);
  }

  filter({ queryData, page, includeSlaEvents = false }) {
    return axios.post(`${this.url}/filter`, queryData, {
      params: {
        page,
        include_sla_events: includeSlaEvents,
      },
    });
  }

  search({ q, page = 1, includeMeta = true, includeSlaEvents = false }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page,
        include_meta: includeMeta,
        include_sla_events: includeSlaEvents,
      },
    });
  }

  toggleStatus({
    conversationId,
    status,
    snoozedUntil = null,
    caseCloseType = null,
  }) {
    const payload = {
      status,
      snoozed_until: snoozedUntil,
    };
    if (caseCloseType) {
      payload.case_close_type = caseCloseType;
    }

    return axios.post(`${this.url}/${conversationId}/toggle_status`, payload);
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(
      `${this.url}/${conversationId}/assignments?assignee_id=${agentId}`,
      {}
    );
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  assignIssue({ conversationId, issueId }) {
    return axios.post(`${this.url}/${conversationId}/assign_issue`, {
      issue_id: issueId,
    });
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate, typingPreview = '' }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
      typing_preview: typingPreview,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({
    inboxId,
    status,
    assigneeType,
    userIds,
    labelId,
    labels,
    teamId,
    conversationType,
    vipLevelId,
    issueId,
    priority,
  }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        user_ids: userIds,
        label_id: labelId,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
        vip_level_id: vipLevelId,
        issue_id: issueId,
        priority,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
